import {
  deleteLine,
  listRouteCalendarMonths,
  listRoutes,
  listRouteServiceDays,
  loadMonthlyReportData,
  replaceRouteServiceDays,
} from './repository.js';
import { exportMonthlyAggregateReport } from './report-service.js';

let context = null;
let initialized = false;
let refreshToken = 0;
let cachedRoutes = [];
let cachedServiceDays = [];
let cachedCalendarMonths = [];

const SERVICE_PATTERNS = {
  all: {
    label:'Cały miesiąc',
    outbound:new Set([0,1,2,3,4,5,6]),
    return:new Set([0,1,2,3,4,5,6]),
  },
  five: {
    label:'5 dni w tygodniu',
    outbound:new Set([0,3,4,5,6]), // śr, czw, pt, sob, nd
    return:new Set([0,1,4,5,6]),   // czw, pt, sob, nd, pon
  },
  four: {
    label:'4 dni w tygodniu',
    outbound:new Set([0,4,5,6]),   // czw, pt, sob, nd
    return:new Set([0,1,5,6]),     // pt, sob, nd, pon
  },
  three: {
    label:'3 dni w tygodniu',
    outbound:new Set([0,5,6]),     // pt, sob, nd
    return:new Set([0,1,6]),       // sob, nd, pon
  },
  none: {
    label:'Nie kursowało',
    outbound:new Set(),
    return:new Set(),
  },
};

export function initLineManager(options = {}) {
  context = options;
  if (initialized) return;
  initialized = true;

  const monthInput = document.getElementById('monthlyReportMonth');
  const carrierFilter = document.getElementById('monthlyCarrierFilter');
  const refreshButton = document.getElementById('refreshLineManagerBtn');
  const exportButton = document.getElementById('exportMonthlyReportBtn');

  if (monthInput && !monthInput.value) monthInput.value = currentMonth();
  monthInput?.addEventListener('change', () => refreshLineManager());
  carrierFilter?.addEventListener('change', () => renderLineManager());
  refreshButton?.addEventListener('click', () => refreshLineManager());
  exportButton?.addEventListener('click', exportMonthlyReport);
}

export async function refreshLineManager() {
  if (!initialized) return;
  const organizationId = context?.getOrganizationId?.();
  const list = document.getElementById('lineCalendarsList');
  const exportButton = document.getElementById('exportMonthlyReportBtn');
  const refreshButton = document.getElementById('refreshLineManagerBtn');

  if (!organizationId) {
    cachedRoutes = [];
    cachedServiceDays = [];
    cachedCalendarMonths = [];
    if (list) list.innerHTML = '<div class="empty">Zaloguj się do Supabase, aby zarządzać kalendarzem linii.</div>';
    if (exportButton) exportButton.disabled = true;
    setManagerStatus('Brak aktywnej organizacji.', 'neutral');
    return;
  }

  const token = ++refreshToken;
  const month = selectedMonth();
  const previousRefreshText = refreshButton?.textContent || 'Odśwież dane';
  if (refreshButton) {
    refreshButton.disabled = true;
    refreshButton.textContent = 'Odświeżam…';
  }
  if (exportButton) exportButton.disabled = true;
  setManagerStatus('Wczytuję zapisane linie i kalendarz przejazdów…', 'working');

  try {
    const [routes, serviceDays, calendarMonths] = await Promise.all([
      listRoutes(organizationId),
      listRouteServiceDays(organizationId, month),
      listRouteCalendarMonths(organizationId, month),
    ]);

    if (token !== refreshToken) return;
    cachedRoutes = routes || [];
    cachedServiceDays = serviceDays || [];
    cachedCalendarMonths = calendarMonths || [];
    renderLineManager();

    const scheduled = cachedServiceDays.length;
    const lineCount = groupRoutes(
      cachedRoutes.filter(route => route.route_number && route.carrier && normalizeDirectionType(route.direction_type))
    ).length;
    setManagerStatus(
      `Miesiąc ${month}: ${lineCount} linii, ${scheduled} zapisanych przejazdów.`,
      'ok'
    );
  } catch (error) {
    console.error(error);
    if (list) list.innerHTML = '<div class="empty">Nie udało się wczytać kalendarza linii.</div>';
    if (exportButton) exportButton.disabled = true;
    setManagerStatus(`Błąd kalendarza: ${error.message}`, 'error');
  } finally {
    if (token === refreshToken && refreshButton) {
      refreshButton.disabled = false;
      refreshButton.textContent = previousRefreshText;
    }
  }
}

function renderLineManager() {
  const list = document.getElementById('lineCalendarsList');
  const exportButton = document.getElementById('exportMonthlyReportBtn');
  if (!list) return;

  const filter = String(document.getElementById('monthlyCarrierFilter')?.value || '').toUpperCase();
  const completeRoutes = cachedRoutes.filter(route => route.route_number && route.carrier && normalizeDirectionType(route.direction_type));
  const incompleteCount = cachedRoutes.length - completeRoutes.length;
  const groups = groupRoutes(completeRoutes)
    .filter(group => !filter || group.carrier === filter);

  list.textContent = '';
  if (incompleteCount) {
    const warning = document.createElement('div');
    warning.className = 'status warning';
    warning.textContent = `${incompleteCount} starszych tras nie ma jeszcze przewoźnika albo numeru linii. Otwórz je, wybierz INTER/UNIBUS, typ kursu i zapisz ponownie.`;
    list.appendChild(warning);
  }

  if (!groups.length) {
    const empty = document.createElement('div');
    empty.className = 'empty';
    empty.textContent = filter
      ? `Brak zapisanych linii przewoźnika ${filter}.`
      : 'Brak linii gotowych do kalendarza. Zapisz przynajmniej jedną trasę z przewoźnikiem i typem kursu.';
    list.appendChild(empty);
    if (exportButton) exportButton.disabled = true;
    return;
  }

  for (const group of groups) list.appendChild(createLineCard(group));
  if (exportButton) exportButton.disabled = !canExportCurrentFilter();
}

function createLineCard(group) {
  const card = document.createElement('article');
  card.className = 'line-calendar-card';
  card.dataset.lineNumber = group.lineNumber;
  card.dataset.carrier = group.carrier;
  card.dataset.calendarDirty = 'false';

  const header = document.createElement('div');
  header.className = 'line-calendar-header';
  const info = document.createElement('div');
  const primaryRoute = group.routes.find(route => normalizeDirectionType(route.direction_type) === 'outbound') || group.routes[0];
  const lineTitle = String(primaryRoute?.name || primaryRoute?.direction || '').trim();
  info.innerHTML = `
    <div class="line-calendar-title-row">
      <h3>${escapeHtml(group.lineNumber)}</h3>
      <span class="carrier-chip carrier-${group.carrier.toLowerCase()}">${escapeHtml(group.carrier)}</span>
    </div>
    ${lineTitle && lineTitle !== group.lineNumber ? `<p>${escapeHtml(lineTitle)}</p>` : ''}
  `;

  const actions = document.createElement('div');
  actions.className = 'line-calendar-actions';

  const calendarBody = document.createElement('div');
  calendarBody.className = 'line-calendar-body';
  const saved = isCalendarSaved(group);

  const toggleButton = document.createElement('button');
  toggleButton.className = 'button secondary small calendar-toggle-button';
  toggleButton.type = 'button';
  toggleButton.addEventListener('click', () => {
    setCalendarCollapsed(card, calendarBody, toggleButton, !calendarBody.hidden);
  });

  const deleteButton = document.createElement('button');
  deleteButton.className = 'button danger small';
  deleteButton.type = 'button';
  deleteButton.textContent = 'Usuń linię';
  deleteButton.addEventListener('click', () => removeLine(group, deleteButton));

  actions.append(toggleButton, deleteButton);
  header.append(info, actions);

  const summary = document.createElement('div');
  summary.className = 'line-calendar-save-summary';
  summary.dataset.role = 'calendar-summary';

  const legend = document.createElement('div');
  legend.className = 'calendar-legend';
  legend.innerHTML = '<span><b>W</b> wyjazd</span><span><b>P</b> powrót</span><span>Możesz ręcznie zmienić pojedynczy dzień.</span>';

  const calendar = buildCalendar(group, { saved });
  const footer = document.createElement('div');
  footer.className = 'line-calendar-footer';
  const quickActions = document.createElement('div');
  quickActions.className = 'calendar-quick-actions';

  const patternControl = createPatternControl(card);
  quickActions.append(
    patternControl,
    miniButton('Zaznacz W', () => { setDirectionChecks(card, 'outbound', true); setPatternControlCustom(card); markCalendarDirty(card); }),
    miniButton('Zaznacz P', () => { setDirectionChecks(card, 'return', true); setPatternControlCustom(card); markCalendarDirty(card); }),
    miniButton('Wyczyść', () => { applyServicePattern(card, 'none'); markCalendarDirty(card); }),
  );
  const saveButton = document.createElement('button');
  saveButton.className = 'button success small';
  saveButton.type = 'button';
  saveButton.textContent = 'Zapisz kalendarz';
  saveButton.dataset.role = 'calendar-save';
  saveButton.addEventListener('click', () => saveCalendar(card, group, saveButton));
  footer.append(quickActions, saveButton);

  calendarBody.append(legend, calendar, footer);
  calendarBody.addEventListener('change', event => {
    if (event.target?.matches('input[type="checkbox"][data-date]')) {
      setPatternControlCustom(card);
      markCalendarDirty(card);
    }
  });
  card.append(header, summary, calendarBody);

  const initialCounts = saved ? serviceDayCounts(group) : checkedServiceDayCounts(card);
  renderCalendarSummary(summary, initialCounts, saved);
  const patternSelect = card.querySelector('[data-role="calendar-pattern"]');
  if (patternSelect) patternSelect.value = detectCalendarPattern(card);

  setCalendarCollapsed(card, calendarBody, toggleButton, saved);
  return card;
}

function buildCalendar(group, { saved = false } = {}) {
  const month = selectedMonth();
  const [year, monthNumber] = month.split('-').map(Number);
  const dayCount = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const firstWeekday = mondayIndex(new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay());
  const selected = new Set(
    cachedServiceDays
      .filter(day => day.route_number === group.lineNumber && day.carrier === group.carrier)
      .map(day => `${day.service_date}::${normalizeDirectionType(day.direction_type)}`)
  );
  const hasOutbound = group.routes.some(route => normalizeDirectionType(route.direction_type) === 'outbound');
  const hasReturn = group.routes.some(route => normalizeDirectionType(route.direction_type) === 'return');
  const useDefaultWholeMonth = !saved && selected.size === 0;

  const wrapper = document.createElement('div');
  wrapper.className = 'line-calendar-wrap';
  const weekdays = document.createElement('div');
  weekdays.className = 'line-calendar-weekdays';
  for (const label of ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd']) {
    const cell = document.createElement('div');
    cell.textContent = label;
    weekdays.appendChild(cell);
  }
  wrapper.appendChild(weekdays);

  const grid = document.createElement('div');
  grid.className = 'line-calendar-grid';
  for (let index = 0; index < firstWeekday; index += 1) {
    const blank = document.createElement('div');
    blank.className = 'calendar-day blank';
    grid.appendChild(blank);
  }

  for (let day = 1; day <= dayCount; day += 1) {
    const date = `${month}-${String(day).padStart(2, '0')}`;
    const cell = document.createElement('div');
    cell.className = 'calendar-day';

    const dayToggle = document.createElement('button');
    dayToggle.type = 'button';
    dayToggle.className = 'calendar-day-number calendar-day-toggle';
    dayToggle.textContent = String(day);
    dayToggle.title = `Zaznacz albo odznacz jednocześnie wyjazd i powrót: ${date}`;
    dayToggle.setAttribute('aria-label', dayToggle.title);
    dayToggle.addEventListener('click', () => toggleDayDirections(cell));

    const defaultOutbound = useDefaultWholeMonth && shouldRunForPattern(date, 'outbound', 'all');
    const defaultReturn = useDefaultWholeMonth && shouldRunForPattern(date, 'return', 'all');

    cell.append(
      dayToggle,
      calendarCheckbox(date, 'outbound', 'W', hasOutbound, selected.has(`${date}::outbound`) || defaultOutbound),
      calendarCheckbox(date, 'return', 'P', hasReturn, selected.has(`${date}::return`) || defaultReturn),
    );
    grid.appendChild(cell);
  }
  wrapper.appendChild(grid);
  return wrapper;
}

function calendarCheckbox(date, directionType, label, enabled, checked) {
  const wrapper = document.createElement('label');
  wrapper.className = `calendar-direction direction-${directionType}${enabled ? '' : ' disabled'}`;
  wrapper.title = enabled ? `${directionLabel(directionType)} ${date}` : `Najpierw zapisz trasę: ${directionLabel(directionType)}`;
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.dataset.date = date;
  input.dataset.directionType = directionType;
  input.checked = Boolean(checked);
  input.disabled = !enabled;
  const text = document.createElement('span');
  text.textContent = label;
  wrapper.append(input, text);
  return wrapper;
}

function createPatternControl(card) {
  const label = document.createElement('label');
  label.className = 'calendar-pattern-control';
  const text = document.createElement('span');
  text.textContent = 'Kursowanie';
  const select = document.createElement('select');
  select.dataset.role = 'calendar-pattern';
  select.setAttribute('aria-label', 'Schemat kursowania');

  for (const [key, pattern] of Object.entries(SERVICE_PATTERNS)) {
    const option = document.createElement('option');
    option.value = key;
    option.textContent = pattern.label;
    select.appendChild(option);
  }

  const custom = document.createElement('option');
  custom.value = 'custom';
  custom.textContent = 'Własne';
  select.appendChild(custom);

  select.addEventListener('change', () => {
    if (select.value === 'custom') return;
    applyServicePattern(card, select.value);
    markCalendarDirty(card);
  });

  label.append(text, select);
  return label;
}

function applyServicePattern(card, patternKey) {
  if (!SERVICE_PATTERNS[patternKey]) return;
  card.querySelectorAll('input[type="checkbox"][data-date][data-direction-type]:not(:disabled)').forEach(input => {
    input.checked = shouldRunForPattern(input.dataset.date, input.dataset.directionType, patternKey);
  });
  const select = card.querySelector('[data-role="calendar-pattern"]');
  if (select) select.value = patternKey;
}

function detectCalendarPattern(card) {
  for (const key of Object.keys(SERVICE_PATTERNS)) {
    const inputs = [...card.querySelectorAll('input[type="checkbox"][data-date][data-direction-type]:not(:disabled)')];
    if (inputs.length && inputs.every(input => input.checked === shouldRunForPattern(input.dataset.date, input.dataset.directionType, key))) {
      return key;
    }
  }
  return 'custom';
}

function setPatternControlCustom(card) {
  const select = card?.querySelector?.('[data-role="calendar-pattern"]');
  if (select) select.value = 'custom';
}

function shouldRunForPattern(dateText, directionType, patternKey) {
  const pattern = SERVICE_PATTERNS[patternKey];
  if (!pattern) return false;

  const normalizedDirection = normalizeDirectionType(directionType);
  if (!normalizedDirection) return false;

  const [year, monthNumber, day] = String(dateText || '')
    .split('-')
    .map(Number);

  if (!year || !monthNumber || !day) return false;

  const weekday = new Date(
    Date.UTC(year, monthNumber - 1, day)
  ).getUTCDay();

  return pattern[normalizedDirection]?.has(weekday) || false;
}


async function saveCalendar(card, group, button) {
  const organizationId = context?.getOrganizationId?.();
  if (!organizationId) return setManagerStatus('Zaloguj się do Supabase.', 'error');

  const entries = [...card.querySelectorAll('input[type="checkbox"][data-date]:checked')].map(input => ({
    service_date:input.dataset.date,
    direction_type:input.dataset.directionType,
  }));
  const previous = button.textContent;
  button.disabled = true;
  button.textContent = 'Zapisuję…';
  setManagerStatus(`Zapisuję kalendarz ${group.lineNumber} (${group.carrier})…`, 'working');
  try {
    await replaceRouteServiceDays(
      organizationId,
      group.lineNumber,
      group.carrier,
      selectedMonth(),
      entries,
    );
    await refreshLineManager();
    setManagerStatus(
      `Kalendarz ${group.lineNumber} zapisany. Liczba przejazdów: ${entries.length}. Duży kalendarz został ukryty.`,
      'ok'
    );
  } catch (error) {
    console.error(error);
    setManagerStatus(`Nie udało się zapisać kalendarza: ${error.message}`, 'error');
  } finally {
    button.disabled = false;
    button.textContent = previous;
  }
}

async function removeLine(group, button) {
  const organizationId = context?.getOrganizationId?.();
  if (!organizationId) return setManagerStatus('Zaloguj się do Supabase.', 'error');

  const directions = group.routes
    .map(route => directionLabel(route.direction_type))
    .filter(Boolean)
    .join(' i ');
  const accepted = window.confirm(
    `Usunąć linię ${group.lineNumber} (${group.carrier})?\n\n` +
    `Zostaną trwale usunięte: ${directions || 'zapisane kierunki'}, wszystkie wersje tras, punkty, naliczone opłaty, zrzuty kalkulatorów oraz kalendarze. Tej operacji nie można cofnąć.`
  );
  if (!accepted) return;

  const previous = button.textContent;
  button.disabled = true;
  button.textContent = 'Usuwam…';
  setManagerStatus(`Usuwam linię ${group.lineNumber} (${group.carrier})…`, 'working');
  try {
    const deletedIds = await deleteLine(organizationId, group.lineNumber, group.carrier);
    await context?.onLineDeleted?.({
      routeIds:deletedIds,
      lineNumber:group.lineNumber,
      carrier:group.carrier,
    });
    await refreshLineManager();
    setManagerStatus(`Linia ${group.lineNumber} (${group.carrier}) została usunięta z bazy.`, 'ok');
  } catch (error) {
    console.error(error);
    setManagerStatus(`Nie udało się usunąć linii: ${error.message}`, 'error');
  } finally {
    button.disabled = false;
    button.textContent = previous;
  }
}

async function exportMonthlyReport() {
  const organizationId = context?.getOrganizationId?.();
  if (!organizationId) return setManagerStatus('Zaloguj się do Supabase.', 'error');
  const button = document.getElementById('exportMonthlyReportBtn');
  const previous = button?.textContent || 'Pobierz raport zbiorczy';
  if (button) {
    button.disabled = true;
    button.textContent = 'Tworzę Excel…';
  }
  const month = selectedMonth();
  const carrier = String(document.getElementById('monthlyCarrierFilter')?.value || '').toUpperCase();
  setManagerStatus('Pobieram trasy, naliczone opłaty i dni przejazdów z Supabase…', 'working');
  try {
    const data = await loadMonthlyReportData(organizationId, month, carrier);
    await exportMonthlyAggregateReport({ month, carrier, ...data });
    setManagerStatus('Raport został wygenerowany: arkusz zestawienie oraz osobny załącznik dla każdej linii.', 'ok');
  } catch (error) {
    console.error(error);
    setManagerStatus(`Nie udało się przygotować raportu zbiorczego: ${error.message}`, 'error');
  } finally {
    if (button) {
      button.textContent = previous;
      button.disabled = !canExportCurrentFilter();
    }
  }
}

function groupRoutes(routes) {
  const groups = new Map();
  for (const route of routes) {
    const lineNumber = String(route.route_number || route.name || '').trim();
    const carrier = String(route.carrier || '').trim().toUpperCase();
    if (!lineNumber || !carrier) continue;
    const key = `${carrier}::${lineNumber}`;
    if (!groups.has(key)) groups.set(key, { lineNumber, carrier, routes:[] });
    groups.get(key).routes.push(route);
  }
  return [...groups.values()]
    .map(group => ({
      ...group,
      routes:group.routes.sort((a, b) => directionOrder(a.direction_type) - directionOrder(b.direction_type)),
    }))
    .sort((a, b) => a.carrier.localeCompare(b.carrier) || a.lineNumber.localeCompare(b.lineNumber, 'pl', { numeric:true }));
}

function isCalendarSaved(group) {
  const routeIds = group.routes.map(route => route.id).filter(Boolean);
  if (!routeIds.length) return false;
  const savedRouteIds = new Set(
    cachedCalendarMonths
      .map(marker => marker.route_id)
      .filter(Boolean)
  );
  return routeIds.every(routeId => savedRouteIds.has(routeId));
}

function serviceDayCounts(group) {
  const rows = cachedServiceDays.filter(day =>
    day.route_number === group.lineNumber
    && day.carrier === group.carrier
  );
  const outbound = rows.filter(day => normalizeDirectionType(day.direction_type) === 'outbound').length;
  const returnRoute = rows.filter(day => normalizeDirectionType(day.direction_type) === 'return').length;
  return { outbound, returnRoute, total:outbound + returnRoute };
}

function renderCalendarSummary(summary, counts, saved) {
  if (!summary) return;
  summary.className = `line-calendar-save-summary ${saved ? 'saved' : 'unsaved'}`;
  summary.innerHTML = saved
    ? `<strong>Kalendarz zapisany</strong><span>Wyjazdy: ${counts.outbound} · Powroty: ${counts.returnRoute} · Razem: ${counts.total}</span>`
    : `<strong>Niezapisane zmiany</strong><span>Wyjazdy: ${counts.outbound} · Powroty: ${counts.returnRoute} · Razem: ${counts.total}</span>`;
}

function checkedServiceDayCounts(card) {
  const outbound = card.querySelectorAll('input[data-direction-type="outbound"]:checked').length;
  const returnRoute = card.querySelectorAll('input[data-direction-type="return"]:checked').length;
  return { outbound, returnRoute, total:outbound + returnRoute };
}

function markCalendarDirty(card) {
  if (!card) return;
  card.dataset.calendarDirty = 'true';
  const summary = card.querySelector('[data-role="calendar-summary"]');
  renderCalendarSummary(summary, checkedServiceDayCounts(card), false);
  const saveButton = card.querySelector('[data-role="calendar-save"]');
  if (saveButton) saveButton.textContent = 'Zapisz zmiany';
}

function canExportCurrentFilter() {
  const filter = String(document.getElementById('monthlyCarrierFilter')?.value || '').toUpperCase();
  return cachedServiceDays.some(day => !filter || String(day.carrier || '').toUpperCase() === filter);
}

function setCalendarCollapsed(card, body, toggleButton, collapsed) {
  body.hidden = Boolean(collapsed);
  card.classList.toggle('calendar-collapsed', Boolean(collapsed));
  toggleButton.textContent = collapsed ? 'Kalendarz' : 'Ukryj';
  toggleButton.setAttribute('aria-expanded', String(!collapsed));
}

function toggleDayDirections(cell) {
  const inputs = [...cell.querySelectorAll('input[data-direction-type]:not(:disabled)')];
  if (!inputs.length) return;
  const shouldCheck = inputs.some(input => !input.checked);
  inputs.forEach(input => {
    input.checked = shouldCheck;
  });
  const card = cell.closest('.line-calendar-card');
  if (card) {
    setPatternControlCustom(card);
    markCalendarDirty(card);
  }
}

function summarizeTolls(routes) {
  const items = [];
  const seen = new Set();
  for (const route of routes) {
    for (const toll of route.tolls_summary || []) {
      const name = toll.calculatorName || calculatorName(toll.calculatorKey) || toll.countryName || toll.countryCode || 'Opłata';
      const key = `${name}::${toll.costText || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push(toll.costText ? `${name}: ${toll.costText}` : `${name}: brak wyniku`);
    }
  }
  return items.length ? items.join(' · ') : 'brak zapisanych wyników — przelicz kalkulatory i zapisz trasę';
}

function directionAvailability(group) {
  const outbound = group.routes.find(route => normalizeDirectionType(route.direction_type) === 'outbound');
  const returnRoute = group.routes.find(route => normalizeDirectionType(route.direction_type) === 'return');
  return [
    outbound ? `✓ wyjazd: ${outbound.name || outbound.direction || 'zapisany'}` : '⚠ brak zapisanej trasy wyjazdu',
    returnRoute ? `✓ powrót: ${returnRoute.name || returnRoute.direction || 'zapisany'}` : '⚠ brak zapisanej trasy powrotu',
  ].join(' · ');
}

function calculatorName(key) {
  return ({
    'etoll-pl':'e-TOLL Polska',
    'a2-awsa-pl':'A2 Autostrada Wielkopolska',
    'a4-sam-pl':'A4 Katowice–Kraków',
    'myto-cz':'MYTO CZ',
    'emyto-sk':'eMyto Słowacja',
    'darsgo-si':'DarsGo Słowenia',
    'asfinag-at':'ASFINAG Austria',
    'hugo-hu':'HU-GO Węgry',
    'hac-hr':'HAC Chorwacja',
    'azm-hr':'Zagreb–Macelj',
    'bina-istra-hr':'Bina-Istra',
    'autostrade-it':'Autostrade Italia',
    'autoroutes-fr':'Autoroutes Francja',
  })[key] || key || '';
}

function miniButton(label, handler) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button secondary tiny';
  button.textContent = label;
  button.addEventListener('click', handler);
  return button;
}

function setDirectionChecks(card, directionType, checked) {
  card.querySelectorAll(`input[data-direction-type="${directionType}"]:not(:disabled)`).forEach(input => {
    input.checked = checked;
  });
}

function setAllChecks(card, checked) {
  card.querySelectorAll('input[type="checkbox"][data-date]:not(:disabled)').forEach(input => {
    input.checked = checked;
  });
}

function setManagerStatus(message, type) {
  const status = document.getElementById('lineManagerStatus');
  if (!status) return;
  status.textContent = message;
  status.className = `status ${type || 'neutral'}`;
}

function selectedMonth() {
  return document.getElementById('monthlyReportMonth')?.value || currentMonth();
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function mondayIndex(jsDay) {
  return jsDay === 0 ? 6 : jsDay - 1;
}

function normalizeDirectionType(value) {
  const text = String(value || '').trim().toLowerCase();
  if (['return', 'powrot', 'powrót'].includes(text)) return 'return';
  if (['outbound', 'wyjazd'].includes(text)) return 'outbound';
  return '';
}

function directionOrder(value) {
  const normalized = normalizeDirectionType(value);
  return normalized === 'outbound' ? 0 : normalized === 'return' ? 1 : 2;
}

function directionLabel(value) {
  const normalized = normalizeDirectionType(value);
  return normalized === 'return' ? 'Powrót' : normalized === 'outbound' ? 'Wyjazd' : 'Nieokreślony';
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;',
  })[character]);
}
